/*
 *
 * Circuit Images
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 11-Sep-2025
 */

// Cards
import visa from 'Assets/circuit/Visa.svg'
import mastercard from 'Assets/circuit/Mastercard.svg'
import paybybank from 'Assets/circuit/PayByBank.svg'
import bancomat from 'Assets/circuit/Bancomat.svg'
import amex from 'Assets/circuit/Amex.svg'
import discover from 'Assets/circuit/Discover.svg'
import dci from 'Assets/circuit/DCI.svg'
import moneta from 'Assets/circuit/Moneta.svg'
import maestro from 'Assets/circuit/Maestro.svg'
import jcb from 'Assets/circuit/JCB.svg'
import unionPay from 'Assets/circuit/UnionPay.svg'
import aura from 'Assets/circuit/Aura.svg'
import agos from 'Assets/circuit/Agos.svg'
import vPay from 'Assets/circuit/VPay.svg'
import klarna from 'Assets/circuit/Klarna.svg'
import visaElectron from 'Assets/circuit/VisaElectron.svg'

// Meal Vouchers
import day from 'Assets/circuit/UpDay.svg'
import ep from 'Assets/circuit/EP.svg'
import pellegrini from 'Assets/circuit/Pellegrini.svg'
import pluxee from 'Assets/circuit/Pluxee.svg'
import repas from 'Assets/circuit/Repas.svg'

// Other Methods
import bPay from 'Assets/circuit/BancomatPay.svg'
import myBank from 'Assets/circuit/MyBank.svg'
import pagoInConto from 'Assets/circuit/PagoinConto.svg'
import paypal from 'Assets/circuit/PayPal.svg'
import pagobancomat from 'Assets/circuit/Bancomat.svg'
import other from 'Assets/circuit/Default.svg'
import aliPay from 'Assets/circuit/Alipay.svg'
import nexi from 'Assets/circuit/Nexi.svg'
import postePay from 'Assets/circuit/PostePay.svg'

const bank = process.env.BANK

// Shared circuit definitions for payment methods with multiple aliases
const sharedCircuits = {
  unionPay: {
    img: unionPay,
    label: 'UnionPay'
  }
}

// Common circuits for all banks
const commonCircuits = {
  VISAMASTERCARD: {
    img: visa,
    img2: mastercard,
    label: 'Visa e Mastercard'
  },
  VISA: {
    img: visa,
    label: 'Visa'
  },
  VISAESTERO: {
    img: visa,
    label: 'Visa Estero'
  },
  VISAITALIA: {
    img: visa,
    label: 'Visa Italia'
  },
  MASTERCARD: {
    img: mastercard,
    label: 'Mastercard'
  },
  PAYBYBANK: {
    img: paybybank,
    label: 'Pay By Bank'
  },
  PIS: {
    img: paybybank,
    label: 'Pay By Bank'
  },
  MASTERCARDITALIA: {
    img: mastercard,
    label: 'Mastercard Italia'
  },
  MASTERCARDESTERO: {
    img: mastercard,
    label: 'Mastercard Estero'
  },
  BANCOMAT: {
    img: bancomat,
    label: 'BANCOMAT'
  },
  AMEX: {
    img: amex,
    label: 'Amex'
  },
  DISCOVER: {
    img: discover,
    label: 'Discover'
  },
  DINERS: {
    img: dci,
    label: 'Diners Club'
  },
  VPAY: {
    img: vPay,
    label: 'VPay'
  },
  MAESTRO: {
    img: maestro,
    label: 'Maestro'
  },
  JCB: {
    img: jcb,
    label: 'JCB'
  },
  UNIONPAY: sharedCircuits.unionPay,
  CUP: sharedCircuits.unionPay,
  AURA: {
    img: aura,
    label: 'Aura'
  },
  AGOS: {
    img: agos,
    label: 'Agos'
  },
  DAY: {
    img: day,
    label: 'Day'
  },
  EP: {
    img: ep,
    label: 'EP'
  },
  PELLEGRINI: {
    img: pellegrini,
    label: 'Pellegrini'
  },
  PLUXEE: {
    img: pluxee,
    label: 'Pluxee'
  },
  REPAS: {
    img: repas,
    label: 'Repas'
  },
  BPAY: {
    img: bPay,
    label: 'BANCOMAT Pay'
  },
  MYBANK: {
    img: myBank,
    label: 'MyBank'
  },
  PAGOINCONTO: {
    img: pagoInConto,
    label: 'PagoinConto'
  },
  PAYPAL: {
    img: paypal,
    label: 'PayPal'
  },
  PAGOBANCOMAT: {
    img: bancomat,
    label: 'PagoBancomat'
  },
  OTHERS: {
    img: other,
    label: 'Altro'
  },
  ALIPAY: {
    img: aliPay,
    label: 'AliPay'
  },
  NEXI: {
    img: nexi,
    label: 'Nexi'
  },
  POSTEPAY: {
    img: postePay,
    label: 'Postepay'
  },
  COBRANDED: {
    img: nexi,
    label: 'Nexi'
  },
  VISA_ELECTRON: {
    img: visaElectron,
    label: 'Visa Electron'
  },
  KLARNA: {
    img: klarna,
    label: 'Klarna'
  }
}

// Nexi-specific circuits (includes MONETA)
const nexiCircuitsMap = {
  ...commonCircuits,
  MONETA: {
    img: moneta,
    label: 'Moneta'
  }
}

const uciCircuitsMap = {
  ...commonCircuits
}

// Get the correct circuits map based on the bank
const getBankCircuits = () => {
  switch (bank) {
    case 'UCI':
      return uciCircuitsMap
    case 'NEXIBUSINESS':
    default:
      return nexiCircuitsMap
  }
}

export const circuitsMap = getBankCircuits()

/**
 * Finds the payment method corresponding to the payment method name.
 * @param {string} paymentMethod - The payment method name (e.g., "VISA", "MASTERCARD", etc.).
 * @returns {object} - An object containing the image and label of the payment method, or a default payment method if not found.
 * @example
 * // Returns { img: visa, label: 'Visa' }
 * findCircuitPaymentMethod('VISA');
 */
export const findCircuitPaymentMethod = (paymentMethod, label = '-') => circuitsMap[paymentMethod] || {
  img: other,
  label
}

export {
  // Cards
  visa,
  mastercard,
  bancomat,
  amex,
  discover,
  dci,
  moneta,
  maestro,
  jcb,
  unionPay,
  aura,
  agos,
  vPay,

  // Meal Vouchers
  day,
  ep,
  pellegrini,
  pluxee,
  repas,

  // Other Methods
  bPay,
  myBank,
  // pagoInConto,
  paybybank,
  paypal,
  pagobancomat,
  other,
  aliPay,
  nexi,
  postePay
}